import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../services/apiService';
import { 
  User, 
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building2,
  Shield,
  Save,
  Eye,
  EyeOff,
  Lock,
  CheckCircle,
  AlertTriangle,
  Home
} from 'lucide-react';

const ProfilePage = () => {
  const { user, updateUserProfile, isReadOnly } = useAuth();
  const navigate = useNavigate();
  
  // États pour les formulaires
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    address: '',
    profession: '',
    zone: '',
    fraternity: '',
    couple_name: '',
    marriage_date: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });

  // États de contrôle
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // États pour les options
  const [fraternities, setFraternities] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Pré-remplir les données du profil
    setProfileData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
      gender: user.gender || '',
      address: user.address || '',
      profession: user.profession || '',
      zone: user.zone || '',
      fraternity: user.fraternity || '',
      couple_name: user.couple_name || '',
      marriage_date: user.marriage_date ? user.marriage_date.split('T')[0] : ''
    });

    // Charger les options
    loadOrganizationOptions();
  }, [user, navigate]);

  const loadOrganizationOptions = async () => {
    try {
      // Utiliser la route authentifiée
      const response = await apiService.get('/members/organization-options');
      
      if (response.fraternities) {
        setFraternities(response.fraternities);
      }
      // Note: zones ne sont plus utilisées dans cette version
      // if (response.zones) {
      //   setZones(response.zones);
      // }
    } catch (error) {
      console.error('Erreur lors du chargement des options:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Supprimer l'erreur quand l'utilisateur tape
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Supprimer l'erreur quand l'utilisateur tape
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateProfile = () => {
    const newErrors = {};
    
    if (!profileData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est requis';
    }
    
    if (!profileData.last_name.trim()) {
      newErrors.last_name = 'Le nom est requis';
    }
    
    if (!profileData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour vérifier si les données ont changé
  const hasProfileChanged = () => {
    const originalData = {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
      gender: user.gender || '',
      address: user.address || '',
      profession: user.profession || '',
      zone: user.zone || '',
      fraternity: user.fraternity || '',
      couple_name: user.couple_name || '',
      marriage_date: user.marriage_date ? user.marriage_date.split('T')[0] : ''
    };

    return JSON.stringify(originalData) !== JSON.stringify(profileData);
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData.current_password) {
      newErrors.current_password = 'Le mot de passe actuel est requis';
    }
    
    if (!passwordData.password) {
      newErrors.password = 'Le nouveau mot de passe est requis';
    } else if (passwordData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (passwordData.password !== passwordData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    console.log('🔄 Début de la mise à jour du profil...');
    console.log('📝 Données actuelles:', profileData);
    console.log('👤 Utilisateur:', user);
    
    if (!validateProfile()) {
      console.log('❌ Validation échouée');
      return;
    }

    // Vérifier si il y a eu des changements
    if (!hasProfileChanged()) {
      console.log('ℹ️ Aucune modification détectée');
      setSuccessMessage('Aucune modification à enregistrer');
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      // Préparer les données à envoyer (sans le rôle et la cotisation pour le profil)
      const updateData = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone: profileData.phone,
        date_of_birth: profileData.date_of_birth,
        gender: profileData.gender,
        address: profileData.address,
        profession: profileData.profession,
        zone: profileData.zone,
        fraternity: profileData.fraternity,
        couple_name: profileData.couple_name,
        marriage_date: profileData.marriage_date,
      };

      console.log('📤 Envoi des données:', updateData);
      console.log('🌐 URL:', '/auth/profile');

      // Vérifier le token d'authentification
      const token = sessionStorage.getItem('fsf_auth_token') || localStorage.getItem('fsf_auth_token');
      if (!token) {
        console.error('❌ Aucun token d\'authentification trouvé');
        setErrors({ general: 'Vous devez être connecté pour modifier votre profil' });
        return;
      }

      console.log('🔑 Token présent:', token.substring(0, 20) + '...');

      // Utiliser la nouvelle route de profil
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      console.log('📡 Réponse HTTP status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('📋 Erreur HTTP:', errorData);
        
        // Si c'est une erreur de validation (422)
        if (response.status === 422 && (errorData.validationErrors || errorData.errors)) {
          // Transformer les erreurs Laravel en format attendu par notre interface
          const validationErrors = {};
          const generalErrors = [];
          
          const errors = errorData.validationErrors || errorData.errors;
          Object.keys(errors).forEach(field => {
            let errorMessage = Array.isArray(errors[field]) ? errors[field][0] : errors[field]; // Prendre le premier message d'erreur
            
            // Personnaliser certains messages d'erreur
            if (field === 'email' && errorMessage.includes('already been taken')) {
              errorMessage = 'Cette adresse email est déjà utilisée par un autre utilisateur';
            }
            
            // Les champs qui ont un affichage spécifique dans le formulaire
            const formFields = ['first_name', 'last_name', 'email', 'phone', 'date_of_birth', 'gender', 'address', 'profession', 'zone', 'fraternity', 'couple_name', 'marriage_date'];
            
            if (formFields.includes(field)) {
              validationErrors[field] = errorMessage;
            } else {
              // Erreurs pour des champs non affichés
              generalErrors.push(`${field}: ${errorMessage}`);
            }
          });
          
          // Si on a des erreurs générales, les ajouter
          if (generalErrors.length > 0) {
            validationErrors.general = 'Erreurs de validation: ' + generalErrors.join(', ');
          }
          
          console.log('📝 Erreurs de validation:', validationErrors);
          setErrors(validationErrors);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(errorData)}`);
      }

      const responseData = await response.json();
      console.log('📥 Réponse reçue:', responseData);
      
      if (responseData.user || responseData.message) {
        console.log('✅ Succès - mise à jour du profil');
        setSuccessMessage('Profil mis à jour avec succès');
        setIsEditing(false);
        
        // Mettre à jour le contexte utilisateur
        if (updateUserProfile) {
          updateUserProfile(responseData.user || updateData);
          console.log('🔄 Contexte utilisateur mis à jour');
        }
      } else {
        console.log('⚠️ Réponse inattendue:', responseData);
        setErrors({ general: 'Réponse inattendue du serveur' });
      }
    } catch (error) {
      console.error('🚨 Erreur lors de la mise à jour du profil:', error);
      
      // Gestion d'erreur plus simple pour fetch
      if (error.message.includes('HTTP error')) {
        setErrors({ general: `Erreur de communication: ${error.message}` });
      } else {
        setErrors({ general: error.message || 'Erreur lors de la mise à jour du profil' });
      }
    } finally {
      setIsLoading(false);
      console.log('🏁 Fin de la mise à jour du profil');
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await apiService.put('/auth/change-password', passwordData);
      
      if (response.message) {
        setSuccessMessage('Mot de passe mis à jour avec succès');
        setPasswordData({
          current_password: '',
          password: '',
          password_confirmation: ''
        });
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du mot de passe:', error);
      
      if (error.status === 422 && error.validationErrors) {
        setErrors(error.validationErrors);
      } else if (error.data && error.data.errors) {
        setErrors(error.data.errors);
      } else {
        setErrors({ password_general: 'Erreur lors de la mise à jour du mot de passe' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Helper pour afficher les valeurs avec fallback
  const displayValue = (value, placeholder = 'Non fourni') => {
    if (!value || value.trim() === '') {
      return placeholder;
    }
    return value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non fourni';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Déterminer la page de retour selon le rôle
  const getBackPath = () => {
    if (['apz', 'sgz', 'tgz', 'mcz'].includes(user?.role)) {
      return '/zone-dashboard';
    } else if (user?.role === 'admin') {
      return '/admin-dashboard';
    }
    return '/dashboard';
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link
                to={getBackPath()}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Retour au Dashboard
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
                <p className="text-sm text-gray-600">
                  Gérer vos informations personnelles
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 text-justify">
              <span className="text-sm text-gray-600">
                {user.first_name} {user.last_name}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                {user.role?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Messages de succès et d'erreur */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {errors.general && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{errors.general}</p>
              </div>
            </div>
          </div>
        )}

        {/* Informations du profil */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <User className="h-5 w-5 text-gray-400 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">Informations du Profil</h2>
              </div>
              {!isEditing && !isReadOnly && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Modifier
                </button>
              )}
            </div>
          </div>

          <form onSubmit={handleProfileUpdate} className="px-6 py-6 text-justify">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Prénom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={profileData.first_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50' : 'bg-white'
                  } ${errors.first_name ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.first_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
                )}
              </div>

              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={profileData.last_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50' : 'bg-white'
                  } ${errors.last_name ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.last_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50' : 'bg-white'
                  } ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
              {/* Téléphone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Téléphone
                </label>
                {!isEditing ? (
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {displayValue(profileData.phone)}
                  </div>
                ) : (
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleInputChange}
                    placeholder="Numéro de téléphone"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                )}
              </div>

              {/* Date de naissance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Date de naissance
                </label>
                {!isEditing ? (
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {formatDate(profileData.date_of_birth)}
                  </div>
                ) : (
                  <input
                    type="date"
                    name="date_of_birth"
                    value={profileData.date_of_birth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                )}
              </div>

              {/* Genre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genre
                </label>
                <select
                  name="gender"
                  value={profileData.gender}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50' : 'bg-white'
                  } border-gray-300`}
                >
                  <option value="">Sélectionner...</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>

              {/* Zone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Zone
                </label>
                <input
                  type="text"
                  name="zone"
                  value={profileData.zone}
                  onChange={handleInputChange}
                  disabled={!isEditing || user.role === 'apz'} // APZ ne peut pas changer sa zone
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing || user.role === 'apz' ? 'bg-gray-50' : 'bg-white'
                  } border-gray-300`}
                />
              </div>

              {/* Fraternité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="h-4 w-4 inline mr-1" />
                  Fraternité
                </label>
                <select
                  name="fraternity"
                  value={profileData.fraternity}                  
                  onChange={handleInputChange}
                  // disabled={!isEditing}
                  disabled={!isEditing || user.role === 'apz'} // APZ ne peut pas changer sa zone
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    !isEditing ? 'bg-gray-50' : 'bg-white'
                  } border-gray-300`}
                >
                  <option value="">Sélectionner une fraternité...</option>
                  {/* {fraternities.map((fraternity) => ( */}
                    <option  value={profileData.fraternity}>
                      {profileData.fraternity}
                    </option>
                  {/* ))} */}
                </select>
              </div>

              {/* Profession */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profession
                </label>
                {!isEditing ? (
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {displayValue(profileData.profession)}
                  </div>
                ) : (
                  <input
                    type="text"
                    name="profession"
                    value={profileData.profession}
                    onChange={handleInputChange}
                    placeholder="Votre profession"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                )}
              </div>

              {/* Nom du couple */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du couple
                </label>
                {!isEditing ? (
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {displayValue(profileData.couple_name)}
                  </div>
                ) : (
                  <input
                    type="text"
                    name="couple_name"
                    value={profileData.couple_name}
                    onChange={handleInputChange}
                    placeholder="Nom du couple"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                )}
              </div>

              {/* Date de mariage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de mariage
                </label>
                {!isEditing ? (
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {formatDate(profileData.marriage_date)}
                  </div>
                ) : (
                  <input
                    type="date"
                    name="marriage_date"
                    value={profileData.marriage_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                )}
              </div>
            </div>

            {/* Adresse */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Home className="h-4 w-4 inline mr-1" />
                Adresse
              </label>
              {!isEditing ? (
                <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 min-h-[80px]">
                  {displayValue(profileData.address) === 'Non fourni' ? (
                    <span className="text-gray-500 italic">Non fourni</span>
                  ) : (
                    <div className="whitespace-pre-wrap">{profileData.address}</div>
                  )}
                </div>
              ) : (
                <textarea
                  name="address"
                  value={profileData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="Adresse complète..."
                />
              )}
            </div>

            {/* Boutons d'action */}
            {isEditing && (
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setErrors({});
                    // Restaurer les données originales
                    setProfileData({
                      first_name: user.first_name || '',
                      last_name: user.last_name || '',
                      email: user.email || '',
                      phone: user.phone || '',
                      date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
                      gender: user.gender || '',
                      address: user.address || '',
                      profession: user.profession || '',
                      zone: user.zone || '',
                      fraternity: user.fraternity || '',
                      couple_name: user.couple_name || '',
                      marriage_date: user.marriage_date ? user.marriage_date.split('T')[0] : ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Changer le mot de passe */}
        {!isReadOnly && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Lock className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Changer le mot de passe</h2>
            </div>
          </div>

          <form onSubmit={handlePasswordUpdate} className="px-6 py-6">
            {errors.password_general && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{errors.password_general}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Mot de passe actuel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe actuel *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.current ? 'text' : 'password'}
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.current_password ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword.current ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.current_password && (
                  <p className="text-red-500 text-sm mt-1">{errors.current_password}</p>
                )}
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.new ? 'text' : 'password'}
                    name="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword.new ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirmation du mot de passe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le nouveau mot de passe *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    name="password_confirmation"
                    value={passwordData.password_confirmation}
                    onChange={handlePasswordChange}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                      errors.password_confirmation ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword.confirm ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password_confirmation && (
                  <p className="text-red-500 text-sm mt-1">{errors.password_confirmation}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Changer le mot de passe
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;